import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import PropTypes from 'prop-types';

import { useOutsideClick } from '../../../ship/utils/custom-hooks';
import DropdownMenu from './dropdown-menu';

import { getClass } from '../../../ship/utils/component-helpers';

const DropdownToggleWrapper = (props: any) => {
  const {
    children,
    menuAlign,
    items,
    selected,
    onSelect,
    multi,
    searchable,
    confirmPrompt,
    onCancel,
    confirmText,
    cancelText,
    disabled,
    formElement,
    matchToggleWidth,
    itemRenderer,
    onChange,
    name,
    virtualize,
    menuWidth,
    onToggle,
    className,
    customPrompt,
    customPromptOnClick,
    selectAllPrompt,
    overflowScroll,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [displayedItems, setDisplayedItems] = useState(items);
  const [currentSelected, setCurrentSelected] = useState(selected);

  const menuRef = useRef<any>(false);
  useOutsideClick(
    menuRef,
    () => {
      if (isOpen) {
        if (confirmPrompt) {
          setCurrentSelected(selected);
        }

        setDisplayedItems(items);
        setIsOpen(false);
      }
    },
    () => {},
    [isOpen] as any,
  );

  useEffect(() => {
    if (confirmPrompt) {
      setCurrentSelected(selected);
    }
  }, [selected, confirmPrompt]);

  useEffect(() => {
    setDisplayedItems(items);
  }, [items]);

  const onItemSelect = (item: any) => {
    let newSelected;
    if (!multi) {
      setIsOpen(false);
      newSelected = item;
    } else {
      const selectedList = confirmPrompt
        ? currentSelected.slice()
        : selected.slice();
      const itemIndex = selectedList.findIndex(
        (selectedItem: any) => selectedItem.value === item.value,
      );

      if (itemIndex !== -1) {
        selectedList.splice(itemIndex, 1);
        newSelected = selectedList;
      } else {
        newSelected = confirmPrompt
          ? [...currentSelected, item]
          : [...selected, item];
      }
    }

    if (confirmPrompt) {
      setCurrentSelected(newSelected);
    } else {
      onSelect(newSelected);

      if (formElement) {
        onChange({
          target: {
            value: multi
              ? newSelected.map((s: any) => s.value)
              : newSelected.value,
            name,
          },
        });
      }
    }
  };

  const onSearch = (query: any) => {
    if (typeof query === 'undefined' || query === null) return;
    const newItems = items.filter((item: any) => {
      return (
        item.label.toLowerCase().includes(query.toLowerCase()) ||
        item.value.toLowerCase().includes(query.toLowerCase())
      );
    });

    setDisplayedItems(newItems);
  };

  const onConfirmClick = () => {
    onSelect(currentSelected);

    if (formElement) {
      onChange({
        target: {
          value: multi
            ? currentSelected.map((s: any) => s.value)
            : currentSelected.value,
          name,
        },
      });
    }

    setIsOpen(false);
  };

  const onCancelClick = () => {
    onCancel();
    setCurrentSelected(selected);
    setIsOpen(false);
  };

  const onCustomClick = () => {
    customPromptOnClick();
    setIsOpen(false);
  };

  const toggleProps = {
    ...children.props,
    disabled: disabled || children.props.disabled,
    onClick: (e: any) => {
      if (children.props.onClick) {
        children.props.onClick(e);
      }
      if (disabled || children.props.disabled) {
        setIsOpen(false);
      } else {
        setIsOpen(!isOpen);
        onToggle(!isOpen);
      }
    },
  };

  const onSelectAll = (selected: any) => {
    if (selected === 'Select All') {
      setCurrentSelected(items);
    } else {
      setCurrentSelected([]);
    }
  };

  //  Checks if child is a native DOM Element or React Component. Prevents console warnings
  if (typeof children.type !== 'string') {
    toggleProps.isOpen = isOpen;
  }

  const ToggleElement = React.cloneElement(children, toggleProps);

  return (
    <div
      data-cy={`base-dropdown-container${name ? '-' + name : ''}`}
      ref={menuRef}
      className={getClass('relative rounded', className, {
        'form-element': formElement,
      })}
    >
      {ToggleElement}
      <DropdownMenu
        items={displayedItems}
        menuAlign={menuAlign}
        onSelect={onItemSelect}
        selected={confirmPrompt ? currentSelected : selected}
        isShowing={isOpen}
        multi={multi}
        onSearch={onSearch}
        searchable={searchable}
        confirmPrompt={confirmPrompt}
        onConfirm={onConfirmClick}
        onCancel={onCancelClick}
        confirmText={confirmText}
        cancelText={cancelText}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        containerRef={menuRef}
        matchToggleWidth={matchToggleWidth}
        itemRenderer={itemRenderer}
        virtualize={virtualize}
        menuWidth={menuWidth}
        customPrompt={customPrompt}
        customPromptOnClick={onCustomClick}
        selectAllPrompt={selectAllPrompt}
        onSelectAll={onSelectAll as any}
        overflowScroll={overflowScroll}
      />
    </div>
  );
};

DropdownToggleWrapper.propTypes = {
  onSelect: PropTypes.func,
  menuAlign: PropTypes.oneOf(['left', 'right', 'lefttop', 'righttop', 'top']),
  multi: PropTypes.bool,
  searchable: PropTypes.bool,
  confirmPrompt: PropTypes.bool,
  onCancel: PropTypes.func,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  formElement: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  className: PropTypes.string,
  name: PropTypes.string,
  selected: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.any,
      }),
      PropTypes.string,
    ]),
  ),
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  matchToggleWidth: PropTypes.bool,
  itemRenderer: PropTypes.func,
  virtualize: PropTypes.bool,
  menuWidth: PropTypes.number,
  onToggle: PropTypes.func,
  customPrompt: PropTypes.string,
  customPromptOnClick: PropTypes.func,
  selectAllPrompt: PropTypes.bool,
  selectAll: PropTypes.func,
  children: PropTypes.node,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.any,
    }),
  ),
  overflowScroll: PropTypes.bool,
};

DropdownToggleWrapper.defaultProps = {
  onSelect: () => {},
  menuAlign: 'right',
  multi: false,
  searchable: false,
  confirmPrompt: false,
  onCancel: () => {},
  confirmText: 'Done',
  cancelText: 'Cancel',
  // selected: [],
  disabled: false,
  onChange: () => {},
  matchToggleWidth: false,
  itemRenderer: null,
  virtualize: false,
  menuWidth: null,
  onToggle: () => {},
  customPrompt: '',
  customPromptOnClick: () => {},
  selectAllPrompt: false,
  selectAll: () => {},
  overflowScroll: false,
};

export default DropdownToggleWrapper;
